import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, PlugZap, CloudDownload, FlaskConical, CalendarClock, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const fmtZeit = (iso) => {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ZeitplanCard = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/zeitplan").then((res) => {
      setStatus(res.data);
      setForm((f) => f || res.data.konfiguration);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const speichern = async () => {
    setSaving(true);
    try {
      const payload = {
        aktiv: form.aktiv,
        modus: form.modus,
        uhrzeit: form.uhrzeit || "06:30",
        intervall_minuten: Math.max(1, parseInt(form.intervall_minuten) || 60),
      };
      const res = await api.put("/zeitplan", payload);
      setStatus(res.data);
      setForm(res.data.konfiguration);
      toast.success(
        payload.aktiv
          ? `Zeitplan aktiviert – nächster Lauf: ${fmtZeit(res.data.naechster_lauf)}`
          : "Zeitplan deaktiviert."
      );
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const jetztAusfuehren = async () => {
    setRunning(true);
    try {
      const res = await api.post("/import/ftp-abholung");
      toast.success(res.data.meldung || "Abholung abgeschlossen.");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Abholung fehlgeschlagen."));
    } finally {
      setRunning(false);
    }
  };

  if (!form)
    return (
      <Card><CardContent className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></CardContent></Card>
    );

  return (
    <Card data-testid="schedule-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-4 w-4" /> Automatische Abholung (Zeitplan)
        </CardTitle>
        <CardDescription>
          Holt neue ÖNORM-Datenträger automatisch vom Sammelwagenbetreiber ab und importiert sie. Zeitzone: Europe/Vienna.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2 pb-2">
            <Switch data-testid="schedule-active-switch" checked={form.aktiv} onCheckedChange={(v) => setForm({ ...form, aktiv: v })} />
            <Label className="text-sm font-medium">{form.aktiv ? "Aktiviert" : "Deaktiviert"}</Label>
          </div>
          <div className="space-y-1.5">
            <Label>Modus</Label>
            <Select value={form.modus} onValueChange={(v) => setForm({ ...form, modus: v })}>
              <SelectTrigger data-testid="schedule-mode-select" className="w-[200px] bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="taeglich">Täglich um Uhrzeit</SelectItem>
                <SelectItem value="intervall">Intervall (Minuten)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.modus === "taeglich" ? (
            <div className="space-y-1.5">
              <Label>Uhrzeit</Label>
              <Input data-testid="schedule-time-input" type="time" value={form.uhrzeit} onChange={(e) => setForm({ ...form, uhrzeit: e.target.value })} className="w-[130px] bg-card" />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Alle … Minuten</Label>
              <Input data-testid="schedule-interval-input" type="number" min="1" value={form.intervall_minuten} onChange={(e) => setForm({ ...form, intervall_minuten: e.target.value })} className="w-[130px] bg-card" />
            </div>
          )}
          <Button data-testid="schedule-save-button" onClick={speichern} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Speichern
          </Button>
          <Button data-testid="schedule-run-now-button" variant="secondary" onClick={jetztAusfuehren} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />} Jetzt abholen
          </Button>
        </div>

        <div className="rounded-lg border bg-secondary/50 px-4 py-3 text-sm flex flex-wrap gap-x-8 gap-y-1">
          <span>
            <span className="text-muted-foreground">Status: </span>
            {status?.konfiguration?.aktiv ? (
              <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]">aktiv</Badge>
            ) : (
              <Badge variant="outline">inaktiv</Badge>
            )}
          </span>
          <span data-testid="schedule-next-run">
            <span className="text-muted-foreground">Nächster Lauf: </span>
            <span className="font-num font-medium">{fmtZeit(status?.naechster_lauf)}</span>
          </span>
        </div>

        {status?.letzte_laeufe?.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">Letzte automatische Läufe</p>
            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="schedule-run-history">
              {status.letzte_laeufe.map((l) => (
                <div key={l.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm gap-3">
                  <div className="min-w-0">
                    <p className="font-num text-xs text-muted-foreground">{fmtZeit(l.zeitpunkt)}</p>
                    <p className="truncate">{l.meldung}</p>
                  </div>
                  <Badge variant={l.erfolg ? "secondary" : "destructive"} className="shrink-0">
                    {l.erfolg ? `${l.neu_importiert} neu` : "Fehler"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FtpForm = ({ cfg, onSave, onTest, saving, testing, icon: Icon, hinweis }) => {
  const [form, setForm] = useState(cfg);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Icon className="h-4 w-4" /> {cfg.bezeichnung}</CardTitle>
        <CardDescription>{hinweis}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-[1fr_110px] gap-3">
          <div className="space-y-1.5">
            <Label>Host</Label>
            <Input data-testid={`ftp-${cfg.typ}-host-input`} value={form.host} onChange={(e) => set("host", e.target.value)} placeholder="ftp.beispiel.at" className="bg-card" />
          </div>
          <div className="space-y-1.5">
            <Label>Port</Label>
            <Input data-testid={`ftp-${cfg.typ}-port-input`} type="number" value={form.port} onChange={(e) => set("port", parseInt(e.target.value) || 21)} className="bg-card" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Benutzer</Label>
            <Input data-testid={`ftp-${cfg.typ}-user-input`} value={form.user} onChange={(e) => set("user", e.target.value)} className="bg-card" autoComplete="off" />
          </div>
          <div className="space-y-1.5">
            <Label>Passwort</Label>
            <Input data-testid={`ftp-${cfg.typ}-password-input`} type="password" value={form.passwort} onChange={(e) => set("passwort", e.target.value)} className="bg-card" autoComplete="new-password" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Verzeichnis</Label>
            <Input data-testid={`ftp-${cfg.typ}-dir-input`} value={form.verzeichnis} onChange={(e) => set("verzeichnis", e.target.value)} placeholder="/" className="bg-card" />
          </div>
          {cfg.typ === "sammelwagen" && (
            <div className="space-y-1.5">
              <Label>Dateimuster</Label>
              <Input value={form.dateimuster} onChange={(e) => set("dateimuster", e.target.value)} placeholder=".dat" className="bg-card" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-5 pt-1">
          <div className="flex items-center gap-2">
            <Switch checked={form.tls} onCheckedChange={(v) => set("tls", v)} />
            <Label className="text-sm">FTPS (TLS)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.passiv} onCheckedChange={(v) => set("passiv", v)} />
            <Label className="text-sm">Passiver Modus</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.aktiv} onCheckedChange={(v) => set("aktiv", v)} />
            <Label className="text-sm">Aktiv</Label>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button data-testid={`ftp-${cfg.typ}-save-button`} onClick={() => onSave(form)} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Speichern
          </Button>
          <Button data-testid={`ftp-${cfg.typ}-test-button`} variant="secondary" onClick={() => onTest(form)} disabled={testing}>
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />} Verbindung testen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminFtpPage() {
  const [configs, setConfigs] = useState(null);
  const [saving, setSaving] = useState("");
  const [testing, setTesting] = useState("");

  useEffect(() => {
    api.get("/ftp-konfiguration").then((res) => setConfigs(res.data)).catch(() => {});
  }, []);

  const speichern = async (typ, form) => {
    setSaving(typ);
    try {
      const payload = { host: form.host, port: form.port, user: form.user, passwort: form.passwort, verzeichnis: form.verzeichnis, dateimuster: form.dateimuster || "", tls: form.tls, passiv: form.passiv, aktiv: form.aktiv };
      await api.put(`/ftp-konfiguration/${typ}`, payload);
      toast.success("FTP-Konfiguration gespeichert.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving("");
    }
  };

  const testen = async (typ, form) => {
    setTesting(typ);
    try {
      await speichern(typ, form);
      const res = await api.post(`/ftp-konfiguration/${typ}/test`);
      if (res.data.erfolg) toast.success(res.data.meldung);
      else toast.error(res.data.meldung);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setTesting("");
    }
  };

  if (!configs)
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const sammelwagen = configs.find((c) => c.typ === "sammelwagen");
  const labor = configs.find((c) => c.typ === "labor");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">FTP-Einstellungen</h1>
        <p className="text-sm text-muted-foreground">Zugangsdaten für die automatische ÖNORM-Abholung und die Übertragung der Reihungsdateien an das Qualitätslabor Österreich.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sammelwagen && (
          <FtpForm cfg={sammelwagen} icon={CloudDownload} saving={saving === "sammelwagen"} testing={testing === "sammelwagen"}
            hinweis="Quelle der ÖNORM-Datenträger (Download). Neue Dateien werden beim Abholen automatisch importiert."
            onSave={(f) => speichern("sammelwagen", f)} onTest={(f) => testen("sammelwagen", f)} />
        )}
        {labor && (
          <FtpForm cfg={labor} icon={FlaskConical} saving={saving === "labor"} testing={testing === "labor"}
            hinweis="Ziel für Reihungsdateien (Upload an das Qualitätslabor Österreich)."
            onSave={(f) => speichern("labor", f)} onTest={(f) => testen("labor", f)} />
        )}
      </div>
      <ZeitplanCard />
    </div>
  );
}
